import axios from "axios";
import { Ref } from "vue";
import { STATIC_CONFIG } from "./config";
import { getAuthHeader, sessionState } from "./session";
import { notifyNetworkError, notifySuccess } from "./utils";
import { Core } from "@minicity/core";


export const uploadFile = async (file: File, allowedTypes?: ("PDF"|"IMAGE")[]) => {
    const formData = new FormData();
    formData.append("file", file);

    const allowedTypesParam = allowedTypes ? `?type=${allowedTypes.join('|')}` : '';

    const uploadId = await axios.post(
        `${STATIC_CONFIG.API_ROOT}/upload${allowedTypesParam}`,
        formData,
        {headers: {'Content-Type': 'multipart/form-data'}}
    )
    .then(resp => resp.data as string, err => { throw err; });

    return {id: uploadId, filename: file.name} as Core.Document;
};

/*
 * Convenience wrappers for axios HTTP calls
 */
export function getFullyQualifiedAPIUrl(path: string) {
    // If path starts with a '/', treat it as an absolute path. Otherwise, prepend network ID
    const networkId = sessionState.session?.networkId || "minicity";
    return path.startsWith("/") ? `${STATIC_CONFIG.API_ROOT}${path}` : `${STATIC_CONFIG.API_ROOT}/networks/${networkId}/${path}`;
}

export async function apiGet<T>(path: string, contentType?: string) {
    return await axios.get(
        getFullyQualifiedAPIUrl(path),
        {headers: addAuthHeader({"Content-Type": contentType || "application/json"})}
    ).then(resp => { return resp.data as T; }, err => { throw err; });
}

export async function apiPut<T>(path: string, body: any, contentType?: string) {
    return await axios.put(
        getFullyQualifiedAPIUrl(path),
        body,
        {headers: addAuthHeader({"Content-Type": contentType || "application/json"})}
    ).then(resp => { return resp.data as T; }, err => { throw err; });
}

export async function apiPutVoid(path: string, body: any, contentType?: string) {
    return await axios.put(
        getFullyQualifiedAPIUrl(path),
        body,
        {headers: addAuthHeader({"Content-Type": contentType || "application/json"})}
    ).then(resp => null, err => { throw err; });
}

export async function apiPost<T>(path: string, body: any, contentType?: string) {
    return await axios.post(
        getFullyQualifiedAPIUrl(path),
        body,
        {headers: addAuthHeader({"Content-Type": contentType || "application/json"})}
    ).then(resp => { return resp.data as T; }, err => { throw err; });
}

export async function apiPostVoid(path: string, body: any, contentType?: string) {
    return await axios.post(
        getFullyQualifiedAPIUrl(path),
        body,
        {headers: addAuthHeader({"Content-Type": contentType || "application/json"})}
    ).then(resp => null, err => { throw err; });
}

export async function apiDelete<T>(path: string, body: any, contentType?: string) {
    return await axios.delete(
        getFullyQualifiedAPIUrl(path),
        {headers: addAuthHeader({"Content-Type": contentType || "application/json"})}
    ).then(resp => { return resp.data as T; }, err => { throw err; });
}

export async function apiDeleteVoid(path: string, contentType?: string) {
    return await axios.delete(
        getFullyQualifiedAPIUrl(path),
        {headers: addAuthHeader({"Content-Type": contentType || "application/json"})}
    ).then(resp => null, err => { throw err; });
}

export const doApiAction = (tracker: Ref<string|null>|null, actionName: string|null, successMessage: string|null, failureMessage: string|null, action: ()=>Promise<any>) => {
    if (tracker) {
        tracker.value = actionName;
    }
  
    action()
    .then(() => {
      if (successMessage) { notifySuccess(successMessage); }
    })
    .catch(err => {
      if (failureMessage) { notifyNetworkError(failureMessage, err); }
    })
    .finally(() => { if (tracker) { tracker.value = null; } });
};

const addAuthHeader = (headers: any) => {
    const val = getAuthHeader();
    return val==null ? headers : {...headers, "Authorization":val};
}